/**
 * Viewport culling for the unified canvas paint loop.
 *
 * The unified path draws every frame as a direct canvas child via
 * drawElementImage on each paint. Frames scrolled far outside the visible
 * viewport cost a full draw (+ effect passes) for pixels nobody sees. Culling
 * skips those: only frames whose world bounds intersect the visible world rect
 * (plus a pre-render margin) get drawn.
 *
 * Pure functions so the geometry is unit-testable without a canvas.
 */
import type { Viewport } from '../store/types';

export interface WorldRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Map the canvas pixel rect (0,0..canvasW,canvasH) into world coordinates under
 * the given viewport, expanded by `marginPx` screen pixels on every side. The
 * margin keeps frames just off-screen painted so they're ready before scrolling
 * them into view.
 */
export function visibleWorldRect(
  canvasW: number,
  canvasH: number,
  vp: Viewport,
  marginPx = 0,
): WorldRect {
  const z = vp.zoom || 1;
  const m = marginPx / z;
  return {
    left: (0 - vp.pan.x) / z - m,
    top: (0 - vp.pan.y) / z - m,
    right: (canvasW - vp.pan.x) / z + m,
    bottom: (canvasH - vp.pan.y) / z + m,
  };
}

/** Does the frame's world-space bounding box intersect the visible rect? */
export function frameInView(
  x: number,
  y: number,
  w: number,
  h: number,
  view: WorldRect,
): boolean {
  return x < view.right && x + w > view.left && y < view.bottom && y + h > view.top;
}
